import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'

import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getPeriods } from '../../../api/periods/get-periods'
import { getRads } from '../../../api/rads/get-rads'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { BudgetAllocationFilters } from '../../../lib/types/models/budget-line/budget-line-filter'
import CountryFlag from '../../elements/country-flag'

type Props = {
  filter?: BudgetAllocationFilters
  onFilterChange: (filter: BudgetAllocationFilters) => void
}

const BudgetAllocationFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: () => getCenters({ version: 'BASIC' })
  })

  const { data: rads } = useQuery({
    queryKey: ['rads'],
    queryFn: () => getRads({ version: 'BASIC' })
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  return (
    <div className="flex flex-col gap-4 pt-2">
      <MultiSelectInput
        selectedOptions={filter?.centerIds?.map((centerId) => ({
          label: centers?.data.find((c) => c.id === centerId)?.name || '',
          value: centerId
        }))}
        searchable
        variant="primary"
        onChange={(centerIds) => onFilterChange({ ...filter, centerIds })}
        options={
          centers?.data.map((center) => ({
            label: center.name,
            value: center.id
          })) || []
        }
        label="Centre"
      />

      <MultiSelectInput
        selectedOptions={filter?.radIds?.map((radId) => ({
          label: rads?.data.find((r) => r.id === radId)?.title || '',
          value: radId
        }))}
        searchable
        variant="primary"
        onChange={(radIds) => {
          onFilterChange({ ...filter, radIds })
        }}
        options={
          rads?.data.map((rad) => ({
            label: rad.title,
            value: rad.id
          })) || []
        }
        label="RAD"
      />

      <DateStringInput
        label="Date de RAD"
        mode="over"
        value={filter?.radDate}
        onChange={(radDate) => onFilterChange({ ...filter, radDate: radDate ?? undefined })}
      />

      <MultiSelectInput
        selectedOptions={filter?.periodIds?.map((periodId) => ({
          label: periods?.data.find((period) => period.id === periodId)?.name ?? '',
          value: periodId
        }))}
        searchable
        variant="primary"
        onChange={(periodIds) => onFilterChange({ ...filter, periodIds })}
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.id
          })) || []
        }
        label="Périodes"
      />

      <MultiSelectInput
        selectedOptions={filter?.countryIds?.map((countryId) => ({
          label: countries?.data.find((country) => country.id === countryId)?.name ?? '',
          value: countryId
        }))}
        searchable
        variant="primary"
        onChange={(countryIds) => onFilterChange({ ...filter, countryIds })}
        options={
          countries?.data.map(({ id, name }) => ({
            label: name,
            value: id,
            custom: <CountryFlag possibleCountry={name} />
          })) || []
        }
        label="Pays"
      />

      <MultiSelectInput
        selectedOptions={filter?.accomodations?.map((accomodation) => ({
          label: accomodationEnumMapping[accomodation],
          value: accomodation
        }))}
        variant="primary"
        onChange={(accomodations) => onFilterChange({ ...filter, accomodations })}
        options={Object.values(AccomodationEnum).map((accomodation) => ({
          label: accomodationEnumMapping[accomodation],
          value: accomodation
        }))}
        label="Types d’hébergement"
      />
    </div>
  )
}

export default BudgetAllocationFilter
